import React, { useEffect, useMemo, useState } from "react";

import { IDChartData } from "../../InsightsDashboard";
import Chart from "src/app/components/Chart/Chart";
import { normalizeText } from "src/utils/methods";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import { Button } from "src/app/components/Button/Button";

import { ReactComponent as TickIcon } from "src/images/tick.svg";
import { ReactComponent as ChevronDownIcon } from "src/images/chevron-down.svg";

import "./IDCharts.scss";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";

interface Props {
  data: IDChartData;
  singleChart?: boolean;
  defaultMetric?: string;
}

const IDCharts = ({ data, singleChart = false, defaultMetric }: Props) => {
  const periods = useMemo(() => Object.keys(data), [data]);

  const [selectedPeriod, setSelectedPeriod] = useState(periods[0]);

  const { printing } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  useEffect(() => {
    if (!periods.includes(selectedPeriod)) {
      setSelectedPeriod(periods[0]);
    }
  }, [periods, selectedPeriod]);

  const metricKeys = useMemo(() => {
    if (!selectedPeriod) return [];
    return Object.keys(data[selectedPeriod]);
  }, [data, selectedPeriod]);

  const [leftMetric, setLeftMetric] = useState("");
  const [rightMetric, setRightMetric] = useState("");

  useEffect(() => {
    if (!metricKeys.length) return;

    const preferredMetric =
      defaultMetric && metricKeys.includes(defaultMetric)
        ? defaultMetric
        : metricKeys[0];

    setLeftMetric(preferredMetric);
    setRightMetric(metricKeys[1] ?? metricKeys[0]);
  }, [metricKeys, defaultMetric]);

  if (!selectedPeriod) {
    return null;
  }

  const getFormat = () => {
    if (selectedPeriod === "monthly") {
      return "MM-YYYY";
    }

    return "DD-MM";
  };

  return (
    <div className="id-charts">
      {periods.length > 1 && !printing && (
        <div className="id-charts__period-selector">
          Period:&nbsp;
          <Dropdown>
            {(open, setOpen) => (
              <>
                <Button
                  variant="white-with-grey-border"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {normalizeText(selectedPeriod)}
                  <ChevronDownIcon />
                </Button>

                <DropdownMenu isOpen={open}>
                  {periods.map((period) => (
                    <DropdownMenuItem
                      onClick={() => {
                        setSelectedPeriod(period);
                        setOpen(false);
                      }}
                    >
                      {normalizeText(period)}
                      {selectedPeriod === period && <TickIcon />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenu>
              </>
            )}
          </Dropdown>
        </div>
      )}

      <div className="id-charts__wrapper">
        <div className="id-charts__item">
          {printing ? (
            leftMetric && (
              <div className="id-charts__print-title">
                {normalizeText(leftMetric)}
              </div>
            )
          ) : (
            <Dropdown>
              {(open, setOpen) => (
                <>
                  <Button
                    variant="white-with-grey-border"
                    size="small"
                    onClick={() => setOpen(!open)}
                  >
                    {normalizeText(leftMetric)}
                    <ChevronDownIcon />
                  </Button>

                  <DropdownMenu isOpen={open}>
                    {metricKeys.map((item) => (
                      <DropdownMenuItem
                        onClick={() => {
                          setLeftMetric(item);
                          setOpen(false);
                        }}
                      >
                        {normalizeText(item)}
                        {leftMetric === item && <TickIcon />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenu>
                </>
              )}
            </Dropdown>
          )}

          {leftMetric && (
            <Chart
              title=""
              header=""
              color="#5D78FF"
              icon=""
              data={data[selectedPeriod][leftMetric]}
              height={220}
              format={getFormat()}
            />
          )}
        </div>

        {!singleChart && (
          <div className="id-charts__item">
            {printing ? (
              rightMetric && (
                <div className="id-charts__print-title">
                  {normalizeText(rightMetric)}
                </div>
              )
            ) : (
              <Dropdown>
                {(open, setOpen) => (
                  <>
                    <Button
                      variant="white-with-grey-border"
                      size="small"
                      onClick={() => setOpen(!open)}
                    >
                      {normalizeText(rightMetric)}
                      <ChevronDownIcon />
                    </Button>

                    <DropdownMenu isOpen={open}>
                      {metricKeys.map((item) => (
                        <DropdownMenuItem
                          onClick={() => {
                            setRightMetric(item);
                            setOpen(false);
                          }}
                        >
                          {normalizeText(item)}
                          {rightMetric === item && <TickIcon />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenu>
                  </>
                )}
              </Dropdown>
            )}

            {rightMetric && (
              <Chart
                title=""
                header=""
                color="#5D78FF"
                icon=""
                data={data[selectedPeriod][rightMetric]}
                height={220}
                format={getFormat()}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IDCharts;
